import { useState, type ReactNode } from "react";
import { cn } from "./cn";
import { dersRengi } from "./ders-renkleri";
import { CubukGrafik } from "./grafik/CubukGrafik";
import { Ikon } from "./Ikon";
import { Sekmeler } from "./Sekmeler";
import { DEGRADE_BASLIK_SINIFI } from "./Tablo";
import type { GrafikNoktasi } from "./grafik/tipler";

/**
 * DETAYLI ANALİZ GÖRÜNÜMÜ — F-ANL-08b (04.10.2026, müşteri revizesi)
 *
 * Panel (koç) ve öğrenci uygulaması AYNI görünümü kullanıyor: müşteri
 * "öğrenci panelinde de detaylı analiz olsun" dedi ve iki ayrı çizim,
 * koçla öğrencinin aynı haftaya bakıp farklı rakam gördüğü güne giden
 * en kısa yoldu. Veriyi çeken her uygulamanın kendisi; bu bileşen
 * yalnızca çiziyor.
 *
 * Tipler sunucunun `analiz-detay.ts` cevabıyla AYNI şekil. `null`
 * oranlar "ölçülemedi" demek ve "—" yazılıyor, %0 DEĞİL.
 */
export interface AnalizSayaci {
  cozulen: number;
  dogru: number;
  yanlis: number;
  bos: number;
  sureSaniye: number;
  basariOrani: number | null;
}

export interface AnalizFarki {
  cozulen: number;
  dogru: number;
  yanlis: number;
  bos: number;
  basariPuani: number | null;
}

export interface DetayliAnalizVerisi {
  haftaBasi: string;
  haftaSonu: string;
  oncekiHaftaBasi: string;
  oncekiHaftaSonu: string;
  hafta: {
    toplam: AnalizSayaci;
    gunler: Array<{
      tarih: string;
      toplam: AnalizSayaci;
      /*
        Dalga 3 (grafik) alanları. İSTEĞE BAĞLI: eski sunucu cevabında yoklar;
        yoksa grafik hedef çizmez ve "veri girildi mi"yi satırlardan çıkarır.
      */
      /** Mazeretsiz, hedefli görevlerin soru hedefi toplamı; hiç hedef yoksa `null`. */
      hedefSoru?: number | null;
      /** Mazeretsiz ama hedefi yazılmamış görev sayısı — >0 ise hedef KISMİ. */
      hedefsizGorev?: number;
      gorevSayisi?: number;
      veriGirilenGorev?: number;
      satirlar: Array<{
        dersId: string;
        ders: string;
        konu: string | null;
        kaynak: string | null;
        hedefSoru: number | null;
        durum: string;
        mazeretli: boolean;
        veriGirildi: boolean;
        sayac: AnalizSayaci;
      }>;
    }>;
    dersler: Array<{
      dersId: string;
      ders: string;
      sayac: AnalizSayaci;
      konular: Array<{ konuId: string | null; konu: string; sayac: AnalizSayaci }>;
    }>;
    ozet: {
      enCokSoruDers: { ders: string; cozulen: number } | null;
      enCokSoruKonu: { ders: string; konu: string; cozulen: number } | null;
      enYuksekBasariDers: { ders: string; basariOrani: number } | null;
    };
  };
  karsilastirma: {
    toplam: { onceki: AnalizSayaci; simdiki: AnalizSayaci; fark: AnalizFarki };
    dersler: Array<{
      dersId: string;
      ders: string;
      onceki: AnalizSayaci;
      simdiki: AnalizSayaci;
      fark: AnalizFarki;
    }>;
    konular: Array<{
      dersId: string;
      ders: string;
      konuId?: string | null;
      konu?: string;
      onceki: AnalizSayaci;
      simdiki: AnalizSayaci;
      fark: AnalizFarki;
    }>;
  };
}

const GUN_ADI = ["Pazar", "Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi"];

/**
 * Gün kısa adları, PAZARTESİ'DEN başlayarak (hafta sırası).
 *
 * `gun.slice(0, 3)` YAPILMAZ: "Pazartesi" ile "Pazar" ikisi de "Paz",
 * "Cumartesi" ile "Cuma" ikisi de "Cum" olur — grafik ekseninde iki ayrı
 * gün aynı etiketle çıkıyordu (rol testi H4). Yedisi birbirinden farklı
 * ve tek yerde; öğrenci haftalık programı da bunu kullanıyor.
 */
export const GUN_KISA_ADLARI: readonly string[] = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

/** `YYYY-AA-GG` → "Pzt" … "Paz" (yedisi benzersiz). Geçersiz tarihte boş. */
export function gunKisaAdi(tarih: string): string {
  const [y, a, g] = tarih.slice(0, 10).split("-").map(Number) as [number, number, number];
  const gun = new Date(Date.UTC(y, a - 1, g)).getUTCDay();
  return GUN_KISA_ADLARI[(gun + 6) % 7] ?? "";
}

/** `YYYY-AA-GG` → "Pazartesi · 28.09" (saat dilimi kaymadan). */
export function gunBasligi(tarih: string): { gun: string; kisa: string } {
  const [y, a, g] = tarih.slice(0, 10).split("-").map(Number) as [number, number, number];
  const d = new Date(Date.UTC(y, a - 1, g));
  return {
    gun: GUN_ADI[d.getUTCDay()] ?? "",
    kisa: `${String(g).padStart(2, "0")}.${String(a).padStart(2, "0")}`,
  };
}

/** Süre: "2 sa 05 dk", "45 dk", "0 dk". Saniye gösterilmiyor — öğrenci sayacı dakikalık okunur. */
export function sureMetni(saniye: number): string {
  const dk = Math.round(saniye / 60);
  if (dk < 60) return `${dk} dk`;
  return `${Math.floor(dk / 60)} sa ${String(dk % 60).padStart(2, "0")} dk`;
}

/**
 * Yerel takvim günü `YYYY-AA-GG`. `toISOString()` DEĞİL: Türkiye UTC+3 ve
 * gece 00:00–02:59 arası UTC hâlâ dünü gösteriyor — bu depoda aynı hata
 * program ekranında bir hafta geri atıyordu.
 */
export function yerelBugun(simdi: Date = new Date()): string {
  return `${simdi.getFullYear()}-${String(simdi.getMonth() + 1).padStart(2, "0")}-${String(simdi.getDate()).padStart(2, "0")}`;
}

export function yuzde(oran: number | null): string {
  return oran === null ? "—" : `%${Math.round(oran * 100)}`;
}

/** Δ metni: "+12", "−5", "0". Eksi işareti gerçek eksi (U+2212), tire değil. */
export function farkMetni(n: number | null, sonek = ""): string {
  if (n === null) return "—";
  if (n > 0) return `+${n}${sonek}`;
  if (n < 0) return `−${Math.abs(n)}${sonek}`;
  return `0${sonek}`;
}

/**
 * Δ'nın rengi YÖNE göre: doğru ve başarı ARTARSA iyi, yanlış ARTARSA
 * kötü. Referans ekran yanlıştaki düşüşü yeşil gösteriyordu ve bu
 * doğruydu; renk sayının işaretine değil anlamına bağlı.
 */
function farkTonu(n: number | null, artisIyi: boolean): string {
  if (n === null || n === 0) return "text-notr-600";
  return n > 0 === artisIyi ? "text-onay-700" : "text-hata-700";
}

/*
  ================================================================
  UNİKOÇ GÖRÜNÜMÜ — sürüm 9a C1 (referans: Reports, Reports-Progress; tarama-1 §3.16)
  ================================================================
  Eski panelde gün kartı: beyaz yuvarlak kart, ÜSTTE lavanta başlık bandı (gün adı kalın, tarih parantezde,
  sağda dört sayı hapı + süre hapı), altta ders satırları (renkli ders chip'i + konu + "Soru" bilgisi + aynı
  hap dizisi). Karşılaştırma ekranı: üstte "Önceki hafta" (mavi) ve "Güncel hafta" (mor) hapları, özet kartları
  ("800 → 1115"), tablolar degrade başlıklı, ders adı renkli hapta, Δ yeşil ↑ / kırmızı ↓.
  Veri sözleşmesi DEĞİŞMEDİ; bu panel (koç) ve öğrenci uygulaması için ORTAK çizim.
  Hap simgeleri metin glifi (aria-hidden) + gizli ad: sözlükte "soru" ve "çarpı" kavramları yok, her hap için
  yeni ikon eklemek sözlüğü şişirirdi; ekran okuyucu adı (`sr-only`) okur, glif okunmaz.
*/
const KART_KABUGU =
  "border-mor-kenar bg-notr-0 min-w-0 rounded-[1.5rem] border shadow-(--golge-unk-kart)";

/** Tek sayı hapı: glif + gizli ad + değer. */
function SayiHapi({
  glif,
  ad,
  deger,
  ton,
}: {
  glif: string;
  ad: string;
  deger: ReactNode;
  ton?: string;
}) {
  return (
    <span className="bg-mor-50 ring-mor-kenar inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ring-1 ring-inset">
      <span
        aria-hidden="true"
        className={cn("text-[0.8125rem] leading-none", ton ?? "text-metin-ikincil")}
      >
        {glif}
      </span>
      <span className="sr-only">{ad} </span>
      <span className="text-metin-koyu">{deger}</span>
    </span>
  );
}

/** Dört sayı hapı + süre hapı — gün kartında da ders satırında da AYNI dizi. */
function Sayaclar({ s }: { s: AnalizSayaci }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <SayiHapi glif="?" ad="Çözülen" deger={s.cozulen} />
      <SayiHapi glif="✓" ad="Doğru" deger={s.dogru} ton="text-onay-700" />
      <SayiHapi glif="✕" ad="Yanlış" deger={s.yanlis} ton="text-hata-700" />
      <SayiHapi glif="−" ad="Boş" deger={s.bos} />
      <span className="bg-mor-50 ring-mor-kenar inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold tabular-nums ring-1 ring-inset">
        <Ikon ad="bekliyor" boyut="kucuk" className="text-metin-ikincil" />
        <span className="sr-only">Süre </span>
        <span className="text-metin-koyu">{sureMetni(s.sureSaniye)}</span>
      </span>
    </div>
  );
}

/** Ders chip'i — tek palet (`ders-renkleri.ts`): aynı ders her ekranda aynı renk. */
function DersChip({ ders }: { ders: string }) {
  const r = dersRengi(ders);
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold",
        r.chip,
      )}
    >
      <span aria-hidden="true" className={cn("size-2 shrink-0 rounded-full", r.nokta)} />
      <span className="min-w-0 break-words">{ders}</span>
    </span>
  );
}

/** Doğru/yanlış/boş dağılım çubuğu — metin zaten söylüyor, çubuk ekran okuyucudan gizli. */
function DagilimCubugu({ s }: { s: AnalizSayaci }) {
  if (s.cozulen === 0) return <div aria-hidden="true" className="bg-notr-100 h-2 rounded-full" />;
  const p = (n: number) => `${(n / s.cozulen) * 100}%`;
  return (
    <div aria-hidden="true" className="bg-notr-100 flex h-2 overflow-hidden rounded-full">
      <div className="bg-onay-600" style={{ width: p(s.dogru) }} />
      <div className="bg-hata-500" style={{ width: p(s.yanlis) }} />
      <div className="bg-notr-300" style={{ width: p(s.bos) }} />
    </div>
  );
}

function OzetKarti({ baslik, deger, ton }: { baslik: string; deger: ReactNode; ton?: string }) {
  return (
    <div className="border-mor-kenar bg-notr-0 rounded-[1.25rem] border p-4 shadow-(--golge-unk-kart)">
      <p className="text-metin-ikincil text-xs font-medium">{baslik}</p>
      <p className={cn("mt-1 text-2xl font-bold tabular-nums", ton ?? "text-metin-koyu")}>
        {deger}
      </p>
    </div>
  );
}

const GORUNUMLER = [
  { anahtar: "gunler", etiket: "Gün gün" },
  { anahtar: "dersler", etiket: "Ders ve konu" },
  { anahtar: "karsilastirma", etiket: "Haftaları karşılaştır" },
] as const;

type Gorunum = (typeof GORUNUMLER)[number]["anahtar"];

export interface DetayliAnalizProps {
  veri: DetayliAnalizVerisi;
  /** Hafta seçici gibi kapsayıcıya ait denetimler — görünüm seçicinin üstünde çizilir. */
  ust?: ReactNode;
}

export function DetayliAnaliz({ veri, ust }: DetayliAnalizProps) {
  const [gorunum, setGorunum] = useState<Gorunum>("gunler");
  const h = veri.hafta;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {ust}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <OzetKarti baslik="Toplam çözülen" deger={h.toplam.cozulen} />
        <OzetKarti baslik="Doğru" deger={h.toplam.dogru} ton="text-onay-700" />
        <OzetKarti baslik="Yanlış" deger={h.toplam.yanlis} ton="text-hata-700" />
        <OzetKarti baslik="Boş" deger={h.toplam.bos} />
        <OzetKarti baslik="Toplam süre" deger={sureMetni(h.toplam.sureSaniye)} />
      </div>

      <OzetCumlesi veri={veri} />

      <GunGunGrafik veri={veri} />

      <Sekmeler
        etiket="Analiz görünümü"
        gorunum="hap"
        secili={gorunum}
        onSec={setGorunum}
        sekmeler={GORUNUMLER.map((g) => ({ deger: g.anahtar, etiket: g.etiket }))}
      />

      {gorunum === "gunler" ? (
        <GunKartlari veri={veri} />
      ) : gorunum === "dersler" ? (
        <DersKartlari veri={veri} />
      ) : (
        <Karsilastirma veri={veri} />
      )}
    </div>
  );
}

/**
 * GÜN GÜN ÇÖZÜLEN SORU — yığılmış doğru/yanlış/boş + günlük soru hedefi
 * (dalga 3, F-ANL-04/08b, F-SCH-10).
 *
 * BİRİM SORU: süre hedefiyle soru hedefi aynı eksene konmuyor (F-SCH-10).
 * VERİ GİRİLMEYEN GÜN "0" DEĞİL: o günün çubukları `null` — grafik
 * "Ölçülmedi" der. Haftada hiç veri yoksa grafik HİÇ çizilmiyor (özet cümlesi
 * zaten "veri girilmemiş" diyor; boş bir eksen bilgi değil gürültü).
 * Hedef KISMİ ise (bazı görevlerde hedef yazılmamış) bunu metinle söylüyor.
 */
function GunGunGrafik({ veri }: { veri: DetayliAnalizVerisi }) {
  const gunler = veri.hafta.gunler;
  const veriGirilen = (g: (typeof gunler)[number]) =>
    g.veriGirilenGorev ?? g.satirlar.filter((s) => s.veriGirildi).length;
  const olculenGun = gunler.filter((g) => veriGirilen(g) > 0);
  if (olculenGun.length === 0) return null;

  const noktalar: GrafikNoktasi[] = gunler.map((g) => {
    const var_ = veriGirilen(g) > 0;
    return {
      tarih: g.tarih.slice(0, 10),
      etiket: gunKisaAdi(g.tarih),
      degerler: var_
        ? { dogru: g.toplam.dogru, yanlis: g.toplam.yanlis, bos: g.toplam.bos }
        : { dogru: null, yanlis: null, bos: null },
      hedef: g.hedefSoru ?? null,
    };
  });
  const hedefliGun = gunler.filter((g) => g.hedefSoru != null && veriGirilen(g) > 0);
  const ulasan = hedefliGun.filter((g) => g.toplam.cozulen >= (g.hedefSoru ?? Infinity)).length;
  const kismiHedef = gunler.some((g) => g.hedefSoru != null && (g.hedefsizGorev ?? 0) > 0);
  const ozet =
    `Bu hafta veri girilen gün: ${olculenGun.length} / ${gunler.length}` +
    (hedefliGun.length > 0
      ? `; soru hedefine ulaşılan gün: ${ulasan} / ${hedefliGun.length}.`
      : "; günlük soru hedefi yazılmamış.");

  return (
    <section aria-label="Gün gün çözülen soru" className="flex flex-col gap-1">
      <CubukGrafik
        baslik="Gün gün çözülen soru"
        birim="soru"
        ozet={ozet}
        noktalar={noktalar}
        seriler={[
          { anahtar: "dogru", etiket: "Doğru", ton: "onay" },
          { anahtar: "yanlis", etiket: "Yanlış", ton: "hata" },
          { anahtar: "bos", etiket: "Boş", ton: "notr" },
        ]}
      />
      {kismiHedef && (
        <p className="text-notr-600 text-xs">
          Bazı görevlerde soru hedefi yazılmamış; o günlerde hedef çizgisi yalnız hedefli görevleri
          gösterir.
        </p>
      )}
    </section>
  );
}

/**
 * ÖZET CÜMLESİ — referans ekrandaki "Bu hafta 1115 soru çözüldü,
 * başarı %49…" satırı. Cümle VERİDEN kuruluyor; ölçülemeyen parça
 * cümleye girmiyor (uydurma "en yüksek doğruluk" yok).
 */
function OzetCumlesi({ veri }: { veri: DetayliAnalizVerisi }) {
  const h = veri.hafta;
  if (h.toplam.cozulen === 0) {
    return (
      <p className={cn(KART_KABUGU, "text-metin-ikincil p-4 text-sm")}>
        Bu hafta henüz soru verisi girilmemiş.
      </p>
    );
  }
  const k = veri.karsilastirma.toplam;
  return (
    <div className={cn(KART_KABUGU, "flex flex-col gap-3 p-4")}>
      <p className="text-metin-koyu text-sm leading-relaxed">
        Bu hafta <strong>{h.toplam.cozulen}</strong> soru çözüldü
        {h.toplam.basariOrani !== null && (
          <>
            , başarı <strong>{yuzde(h.toplam.basariOrani)}</strong>
          </>
        )}
        .
        {h.ozet.enCokSoruDers && (
          <>
            {" "}
            En çok soru <strong>{h.ozet.enCokSoruDers.ders}</strong> dersinde (
            {h.ozet.enCokSoruDers.cozulen}).
          </>
        )}
        {h.ozet.enCokSoruKonu && (
          <>
            {" "}
            En çok çalışılan konu <strong>{h.ozet.enCokSoruKonu.konu}</strong>.
          </>
        )}
        {h.ozet.enYuksekBasariDers && (
          <>
            {" "}
            En yüksek başarı <strong>{h.ozet.enYuksekBasariDers.ders}</strong> (
            {yuzde(h.ozet.enYuksekBasariDers.basariOrani)}).
          </>
        )}{" "}
        Önceki haftaya göre çözülen{" "}
        <strong className={farkTonu(k.fark.cozulen, true)}>{farkMetni(k.fark.cozulen)}</strong>
        {k.fark.basariPuani !== null && (
          <>
            , başarı{" "}
            <strong className={farkTonu(k.fark.basariPuani, true)}>
              {farkMetni(k.fark.basariPuani, " puan")}
            </strong>
          </>
        )}
        .
      </p>
      <DagilimCubugu s={h.toplam} />
      <p className="text-notr-600 flex flex-wrap gap-x-3 text-xs">
        <span>
          <span aria-hidden="true" className="bg-onay-600 mr-1 inline-block size-2 rounded-full" />
          Doğru {yuzde(h.toplam.cozulen ? h.toplam.dogru / h.toplam.cozulen : null)}
        </span>
        <span>
          <span aria-hidden="true" className="bg-hata-500 mr-1 inline-block size-2 rounded-full" />
          Yanlış {yuzde(h.toplam.cozulen ? h.toplam.yanlis / h.toplam.cozulen : null)}
        </span>
        <span>
          <span aria-hidden="true" className="bg-notr-300 mr-1 inline-block size-2 rounded-full" />
          Boş {yuzde(h.toplam.cozulen ? h.toplam.bos / h.toplam.cozulen : null)}
        </span>
      </p>
    </div>
  );
}

/**
 * GÜN GÜN — her gün bir kart (lavanta başlık bandı), her görev bir satır.
 *
 * "Veri girilmedi" ayrı söyleniyor: müşterinin anlattığı akışta
 * öğrenci sayı girmezse gün KIRMIZI, girerse YEŞİL. Geçmiş bir günde
 * girilmemiş görev uyarı tonunda; mazeretli görev nötr.
 */
function GunKartlari({ veri }: { veri: DetayliAnalizVerisi }) {
  const bugun = yerelBugun();
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {veri.hafta.gunler.map((g) => {
        const b = gunBasligi(g.tarih);
        const gecmis = g.tarih < bugun;
        return (
          <section
            key={g.tarih}
            aria-label={`${b.gun} ${b.kisa}`}
            className={cn(KART_KABUGU, "flex flex-col overflow-hidden")}
          >
            <header className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-(image:--gradyan-unk-bant) px-4 py-3">
              <h3 className="text-metin-koyu text-sm font-bold">
                {b.gun} <span className="text-metin-ikincil font-semibold">({b.kisa})</span>
              </h3>
              {g.satirlar.length > 0 && <Sayaclar s={g.toplam} />}
            </header>
            {g.satirlar.length === 0 ? (
              <p className="text-metin-ikincil px-4 py-3 text-sm">Görev yok.</p>
            ) : (
              <ul className="flex flex-col px-4 pb-2">
                {g.satirlar.map((s, i) => (
                  <li
                    key={`${s.dersId}-${i}`}
                    className="border-mor-kenar flex flex-col gap-2 border-t border-dashed py-3 first:border-t-0"
                  >
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <DersChip ders={s.ders} />
                      {s.konu && <span className="text-metin-koyu text-sm">{s.konu}</span>}
                      {s.mazeretli ? (
                        <span className="text-metin-ikincil ml-auto text-xs">Mazeretli</span>
                      ) : !s.veriGirildi ? (
                        <span
                          className={cn(
                            "ml-auto text-xs font-medium",
                            gecmis ? "text-hata-700" : "text-metin-ikincil",
                          )}
                        >
                          Veri girilmedi
                        </span>
                      ) : null}
                    </div>
                    {(s.kaynak || s.hedefSoru !== null) && (
                      <span className="text-metin-ikincil text-xs">
                        {s.kaynak}
                        {s.kaynak && s.hedefSoru !== null ? " • " : ""}
                        {s.hedefSoru !== null ? `Hedef ${s.hedefSoru} soru` : ""}
                      </span>
                    )}
                    {s.veriGirildi && <Sayaclar s={s.sayac} />}
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** DERS VE KONU — ders kartı, konu ayrıntısı `<details>` ile: klavye ve ekran okuyucu ücretsiz. */
function DersKartlari({ veri }: { veri: DetayliAnalizVerisi }) {
  if (veri.hafta.dersler.length === 0) {
    return <p className="text-metin-ikincil text-sm">Bu hafta yayınlanmış görev yok.</p>;
  }
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {veri.hafta.dersler.map((d) => (
        <section
          key={d.dersId}
          aria-label={d.ders}
          className={cn(KART_KABUGU, "flex flex-col gap-3 p-4")}
        >
          <header className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="min-w-0 text-sm">
              <DersChip ders={d.ders} />
            </h3>
            <span className="text-metin-ikincil text-xs">
              Toplam <strong className="text-metin-koyu">{d.sayac.cozulen}</strong> soru · Başarı{" "}
              {yuzde(d.sayac.basariOrani)}
            </span>
          </header>
          <Sayaclar s={d.sayac} />
          <DagilimCubugu s={d.sayac} />
          <details className="group">
            <summary className="border-mor-kesikli text-mor-700 inline-flex min-h-9 cursor-pointer items-center rounded-xl border border-dashed px-3 text-sm font-semibold">
              Konu ayrıntıları ({d.konular.length})
            </summary>
            <ul className="mt-2 flex flex-col">
              {d.konular.map((k) => (
                <li
                  key={k.konuId ?? "-"}
                  className="border-mor-kenar flex flex-col gap-1.5 border-t border-dashed py-2 first:border-t-0"
                >
                  <span className="text-metin-koyu text-sm">{k.konu}</span>
                  <Sayaclar s={k.sayac} />
                </li>
              ))}
            </ul>
          </details>
        </section>
      ))}
    </div>
  );
}

/** Δ oku: renk tek sinyal değil — işaret metinde de var (`farkMetni`); ok yalnız görsel pekiştirme. */
function FarkOku({ n }: { n: number | null }) {
  if (n === null || n === 0) return null;
  return (
    <span aria-hidden="true" className="mr-0.5">
      {n > 0 ? "↑" : "↓"}
    </span>
  );
}

/** Hafta hapı: önceki = mavi, güncel = mor (referans: "Önceki Hafta" / "Güncel Hafta" rozetleri). */
function HaftaHapi({ ad, aralik, onceki }: { ad: string; aralik: string; onceki: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-bold",
        onceki ? "bg-planli-zemin text-planli-metin" : "bg-mor-50 text-mor-700",
      )}
    >
      {ad}: <span className="tabular-nums">{aralik}</span>
    </span>
  );
}

/**
 * HAFTALARI KARŞILAŞTIR — referanstaki "Gelişim takip".
 *
 * Telefonda tablo yerine satır kartı (`md` altı): yedi sütun 390 px'e
 * sığmıyor. Masaüstünde tablo; `overflow-x-auto` ara genişlikte yedek.
 */
function Karsilastirma({ veri }: { veri: DetayliAnalizVerisi }) {
  const k = veri.karsilastirma;
  const onceki = `${gunBasligi(veri.oncekiHaftaBasi).kisa}–${gunBasligi(veri.oncekiHaftaSonu).kisa}`;
  const simdiki = `${gunBasligi(veri.haftaBasi).kisa}–${gunBasligi(veri.haftaSonu).kisa}`;
  const t = k.toplam;
  const OZET: ReadonlyArray<[string, number, number, number]> = [
    ["Çözülen", t.onceki.cozulen, t.simdiki.cozulen, t.fark.cozulen],
    ["Doğru", t.onceki.dogru, t.simdiki.dogru, t.fark.dogru],
    ["Yanlış", t.onceki.yanlis, t.simdiki.yanlis, t.fark.yanlis],
    ["Boş", t.onceki.bos, t.simdiki.bos, t.fark.bos],
  ];
  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className={cn(KART_KABUGU, "flex flex-col gap-3 p-4")}>
        <div className="flex flex-wrap items-center gap-2">
          <HaftaHapi ad="Önceki hafta" aralik={onceki} onceki />
          <span aria-hidden="true" className="text-metin-ikincil">
            →
          </span>
          <HaftaHapi ad="Güncel hafta" aralik={simdiki} onceki={false} />
        </div>
        <p className="text-metin-ikincil text-sm">
          Δ: güncel hafta eksi önceki hafta. Başarı farkı yüzde puanı.
        </p>
        <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {OZET.map(([ad, a, b, f]) => (
            <div
              key={ad}
              className="border-mor-kenar bg-notr-0 rounded-[1.25rem] border p-3 shadow-(--golge-unk-kart)"
            >
              <dt className="text-metin-ikincil text-xs">{ad}</dt>
              <dd className="text-metin-koyu mt-0.5 text-sm font-bold tabular-nums">
                {a} → {b}{" "}
                <span className={cn("font-semibold", farkTonu(f, ad !== "Yanlış"))}>
                  (<FarkOku n={f} />
                  {farkMetni(f)})
                </span>
              </dd>
            </div>
          ))}
        </dl>
      </div>
      <FarkTablosu
        baslik="Ders bazında değişim"
        satirlar={k.dersler.map((d) => ({ anahtar: d.dersId, ad: d.ders, alt: null, ...d }))}
      />
      <FarkTablosu
        baslik="Konu bazında değişim"
        satirlar={k.konular.map((d) => ({
          anahtar: `${d.dersId}|${d.konuId ?? ""}`,
          ad: d.konu ?? "",
          alt: d.ders,
          ...d,
        }))}
      />
    </div>
  );
}

const FARK_BASLIK = "px-2 py-2.5 text-right font-semibold";

function FarkTablosu({
  baslik,
  satirlar,
}: {
  baslik: string;
  satirlar: Array<{
    anahtar: string;
    ad: string;
    alt: string | null;
    onceki: AnalizSayaci;
    simdiki: AnalizSayaci;
    fark: AnalizFarki;
  }>;
}) {
  return (
    <section className={cn(KART_KABUGU, "p-4")}>
      <h3 className="text-metin-koyu mb-3 text-base font-bold">{baslik}</h3>
      {satirlar.length === 0 ? (
        <p className="text-metin-ikincil text-sm">İki haftada da veri yok.</p>
      ) : (
        <>
          {/* Telefon: satır kartı. Yedi sütun 390 px'e sığmıyor, yana kaydırılan tablo okunmuyordu (ölçüldü). */}
          <ul className="flex flex-col md:hidden">
            {satirlar.map((s) => (
              <li
                key={s.anahtar}
                className="border-mor-kenar border-t border-dashed py-3 first:border-t-0 first:pt-0"
              >
                <p className="text-sm font-medium">
                  <DersChip ders={s.ad} />
                </p>
                {s.alt && <p className="text-metin-ikincil mt-0.5 text-xs">{s.alt}</p>}
                <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs tabular-nums">
                  <div>
                    <dt className="text-metin-ikincil">Çözülen</dt>
                    <dd className="text-metin-koyu">
                      {s.onceki.cozulen} → {s.simdiki.cozulen}{" "}
                      <span className={cn("font-semibold", farkTonu(s.fark.cozulen, true))}>
                        (<FarkOku n={s.fark.cozulen} />
                        {farkMetni(s.fark.cozulen)})
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-metin-ikincil">Başarı</dt>
                    <dd className="text-metin-koyu">
                      {yuzde(s.onceki.basariOrani)} → {yuzde(s.simdiki.basariOrani)}{" "}
                      <span className={cn("font-semibold", farkTonu(s.fark.basariPuani, true))}>
                        (<FarkOku n={s.fark.basariPuani} />
                        {farkMetni(s.fark.basariPuani, " p")})
                      </span>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-metin-ikincil">Δ Doğru</dt>
                    <dd className={cn("font-semibold", farkTonu(s.fark.dogru, true))}>
                      <FarkOku n={s.fark.dogru} />
                      {farkMetni(s.fark.dogru)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-metin-ikincil">Δ Yanlış</dt>
                    <dd className={cn("font-semibold", farkTonu(s.fark.yanlis, false))}>
                      <FarkOku n={s.fark.yanlis} />
                      {farkMetni(s.fark.yanlis)}
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[560px] border-collapse text-sm">
              <thead>
                <tr className="text-left text-xs">
                  <th
                    scope="col"
                    className={cn(DEGRADE_BASLIK_SINIFI, "rounded-tl-xl px-3 py-2.5")}
                  >
                    Ders / konu
                  </th>
                  <th scope="col" className={cn(DEGRADE_BASLIK_SINIFI, FARK_BASLIK)}>
                    Çözülen
                  </th>
                  <th scope="col" className={cn(DEGRADE_BASLIK_SINIFI, FARK_BASLIK)}>
                    Δ Çözülen
                  </th>
                  <th scope="col" className={cn(DEGRADE_BASLIK_SINIFI, FARK_BASLIK)}>
                    Δ Doğru
                  </th>
                  <th scope="col" className={cn(DEGRADE_BASLIK_SINIFI, FARK_BASLIK)}>
                    Δ Yanlış
                  </th>
                  <th scope="col" className={cn(DEGRADE_BASLIK_SINIFI, FARK_BASLIK)}>
                    Başarı
                  </th>
                  <th
                    scope="col"
                    className={cn(DEGRADE_BASLIK_SINIFI, FARK_BASLIK, "rounded-tr-xl")}
                  >
                    Δ Başarı
                  </th>
                </tr>
              </thead>
              <tbody className="tabular-nums">
                {satirlar.map((s) => (
                  <tr key={s.anahtar} className="border-mor-kenar border-b last:border-b-0">
                    <th scope="row" className="px-3 py-2.5 text-left font-normal">
                      <DersChip ders={s.ad} />
                      {s.alt && (
                        <span className="text-metin-ikincil mt-0.5 block text-xs">{s.alt}</span>
                      )}
                    </th>
                    <td className="text-metin-koyu px-2 py-2.5 text-right">
                      {s.onceki.cozulen} → {s.simdiki.cozulen}
                    </td>
                    <td
                      className={cn(
                        "px-2 py-2.5 text-right font-semibold",
                        farkTonu(s.fark.cozulen, true),
                      )}
                    >
                      <FarkOku n={s.fark.cozulen} />
                      {farkMetni(s.fark.cozulen)}
                    </td>
                    <td
                      className={cn(
                        "px-2 py-2.5 text-right font-semibold",
                        farkTonu(s.fark.dogru, true),
                      )}
                    >
                      <FarkOku n={s.fark.dogru} />
                      {farkMetni(s.fark.dogru)}
                    </td>
                    <td
                      className={cn(
                        "px-2 py-2.5 text-right font-semibold",
                        farkTonu(s.fark.yanlis, false),
                      )}
                    >
                      <FarkOku n={s.fark.yanlis} />
                      {farkMetni(s.fark.yanlis)}
                    </td>
                    <td className="text-metin-koyu px-2 py-2.5 text-right">
                      {yuzde(s.onceki.basariOrani)} → {yuzde(s.simdiki.basariOrani)}
                    </td>
                    <td
                      className={cn(
                        "px-2 py-2.5 text-right font-semibold",
                        farkTonu(s.fark.basariPuani, true),
                      )}
                    >
                      <FarkOku n={s.fark.basariPuani} />
                      {farkMetni(s.fark.basariPuani, " p")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
